// Console de logs du CRM — côté navigateur.
//
// Tout ce qui est journalisé ici atterrit dans la table `system_logs`, lisible
// dans l'écran Console. Le front n'écrit que pour un utilisateur connecté (RLS) :
// les erreurs avant connexion restent dans la console du navigateur.
import { supabase } from "./supabase";

export type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  stack?: string;
}

const queue: LogEntry[] = [];
let flushing = false;
const SESSION_ID = crypto.randomUUID();

async function flush() {
  if (flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.splice(0, 20);
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return; // pas connecté : on laisse tomber silencieusement
    await supabase.from("system_logs").insert(
      batch.map((e) => ({
        level: e.level,
        source: "web",
        message: e.message.slice(0, 2000),
        context: { ...e.context, url: location.pathname, session: SESSION_ID, ua: navigator.userAgent.slice(0, 200) },
        stack: e.stack?.slice(0, 4000) ?? null,
      })),
    );
  } catch {
    // Ne jamais faire planter l'app à cause du logger.
  } finally {
    flushing = false;
    if (queue.length) void flush();
  }
}

/** Message lisible pour une erreur quelle qu'en soit la forme (Error, PostgrestError, chaîne). */
function describe(err: unknown): { text: string; extra: Record<string, unknown> } {
  if (err instanceof Error) return { text: err.message, extra: { name: err.name } };
  if (err && typeof err === "object") {
    const o = err as Record<string, unknown>;
    const text = [o.message, o.details, o.hint].filter(Boolean).join(" — ");
    return { text: text || JSON.stringify(o).slice(0, 300), extra: { code: o.code, status: o.status } };
  }
  return { text: String(err ?? ""), extra: {} };
}

function log(level: LogLevel, message: string, context?: Record<string, unknown>, err?: unknown) {
  const stack = err instanceof Error ? err.stack : undefined;
  if (import.meta.env.DEV) console[level === "debug" ? "log" : level](`[crm:${level}]`, message, context ?? "", err ?? "");
  if (level === "debug") return; // debug : navigateur seulement
  queue.push({ level, message, context, stack });
  void flush();
}

export const logger = {
  debug: (m: string, c?: Record<string, unknown>) => log("debug", m, c),
  info: (m: string, c?: Record<string, unknown>) => log("info", m, c),
  warn: (m: string, c?: Record<string, unknown>) => log("warn", m, c),
  error: (m: string, err?: unknown, c?: Record<string, unknown>) => {
    const d = err === undefined ? null : describe(err);
    log("error", d && d.text ? `${m} — ${d.text}` : m, { ...c, ...(d?.extra ?? {}) }, err);
  },
};

/** À appeler une fois au démarrage : capte les erreurs non gérées. */
export function installGlobalErrorHandlers() {
  window.addEventListener("error", (e) => {
    // Avertissement inoffensif du navigateur (boucle d'observation de taille
    // sur les menus déroulants), sans conséquence et sans action possible.
    if (/ResizeObserver loop/.test(String(e.message))) return;
    logger.error("Erreur non gérée", e.error ?? e.message, { file: e.filename, line: e.lineno });
  });
  window.addEventListener("unhandledrejection", (e) => logger.error("Promesse rejetée non gérée", e.reason));
}

/** Erreur de saisie côté utilisateur : affichée dans le formulaire, jamais journalisée comme incident. */
export class ValidationError extends Error {
  constructor(message: string) { super(message); this.name = "ValidationError"; }
}
